import { Box, styled, Typography } from '@mui/material';
import { ButtonV2 } from 'ui/ButtonV2';
import { MuiMap } from 'lib/MuiMap';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { FlatAppbar } from 'ui/FlatAppbar';
import { FlatToolbar } from 'ui/FlatToolbar';
import { AddressMapCrosshair } from './forms/AddressMapCrosshair';
import { addressObjectToString } from 'components/delivery/helpers';
import { UserAddress } from 'components/user/model/User';
import { REDUCED_CONTENT_MAX_WIDTH } from 'config/constants';

interface AddressViewProps {
    address: UserAddress | null;
    onClose: () => void;
    onDelete: (id: string) => void;
    loading?: boolean;
}

const MAP_CLASS_NAME = 'AddressView-map';

const StyledBox = styled(Box)({
    position: 'absolute',
    height: '100%',
    width: '100%',
    top: 0,
    left: 0,
    [`& .${MAP_CLASS_NAME}`]: {
        height: '100%',
        width: '100%'
    }
});

export const AddressView: React.FC<AddressViewProps> = ({ address, onClose, onDelete, loading }) => {
    const { t } = useTranslation();
    const handleDeleteClick = React.useCallback(() => {
        if (address) {
            onDelete(address._id);
        }
    }, [address, onDelete]);
    const addressDescription = React.useMemo(() => addressObjectToString(address), [address]);
    const centre = React.useMemo(() => {
        if (address) {
            const [lng, lat] = address.coordinates;
            return { lat, lng };
        }
        return { lat: 0, lng: 0 };
    }, [address]);
    if (!address) {
        return null;
    }
    return (
        <Box maxWidth={REDUCED_CONTENT_MAX_WIDTH} marginX="auto" minHeight="100%">
            <FlatAppbar variant="dense">
                <FlatToolbar
                    title={address.name ?? address?.address?.line1}
                    hideUserAvatar
                    disableEdit
                    onBack={onClose}
                />
            </FlatAppbar>
            <Box width="100%" pb="100%" position="relative">
                <StyledBox>
                    <MuiMap
                        center={centre}
                        width={0}
                        className={MAP_CLASS_NAME}
                        zoom={16}
                        options={{
                            keyboardShortcuts: false,
                            gestureHandling: 'none',
                            disableDefaultUI: true
                        }}
                    />
                    <AddressMapCrosshair />
                </StyledBox>
            </Box>
            <Box p={1} pt={2}>
                <Typography>{addressDescription}</Typography>
                <Typography sx={{ opacity: 0.7 }}>{address.note}</Typography>
                <ButtonV2
                    color="primary"
                    variant="contained"
                    onClick={handleDeleteClick}
                    loading={loading}
                    disabled={loading}
                    sx={{ mt: 1 }}
                    fullWidth
                >
                    {t('DELIVERY_ADDRESS_DELETE_TITLE')}
                </ButtonV2>
            </Box>
        </Box>
    );
};
